import { useEffect } from 'react';
import { X, Check } from 'lucide-react';
import Button from './Button';
import { useDraggableResizable, ResizeHandles } from '../../hooks/useDraggableResizable';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useLanguage } from '../../context/LanguageContext';

/**
 * Modal - Reusable modal component for forms and content
 * Features: Draggable header, resizable from corners and edges
 * On mobile (< 640px): full screen, no drag/resize
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is visible
 * @param {Function} props.onClose - Called when modal is closed
 * @param {string} props.title - Modal title
 * @param {string} props.subtitle - Optional subtitle in header
 * @param {React.ReactNode} props.icon - Icon to display in header
 * @param {string} props.headerColor - Tailwind gradient classes for header bg (default: cabaza purple-pink)
 * @param {React.ReactNode} props.children - Modal content
 * @param {React.ReactNode} props.footer - Custom footer content (overrides default buttons)
 * @param {React.ReactNode} props.footerLeft - Content on the left side of the footer
 * @param {React.ReactNode} props.footerCenter - Content in the center of the footer
 * @param {string} props.submitText - Text for submit button
 * @param {string} props.cancelText - Text for cancel button
 * @param {Function} props.onSubmit - Called when submit button is clicked
 * @param {boolean} props.loading - Show loading state on submit button
 * @param {boolean} props.hideFooter - Hide the footer entirely
 * @param {boolean} props.hideCancel - Hide cancel button
 * @param {'sm'|'md'|'lg'|'xl'|'full'} props.size - Modal size (affects initial dimensions)
 * @param {boolean} props.closeOnOverlayClick - Close modal when clicking overlay (default: true)
 * @param {boolean} props.resizable - Enable resize handles (default: true)
 * @param {boolean} props.draggable - Enable dragging (default: true)
 * @param {boolean} props.noPadding - Remove padding from content area
 * @param {Object} props.wizard - Wizard config: { steps: [{id, icon, label}], currentStep, onStepChange }
 * @param {Object} props.tabs - Tabs config: { items: [{id, icon, label}], activeTab, onChange }
 * @param {React.ReactNode} props.aboveContent - Content rendered between stepper/tabs and the scrollable area
 */
const Modal = ({
    isOpen,
    onClose,
    title,
    subtitle,
    icon: Icon,
    headerColor,
    children,
    footer,
    footerLeft,
    footerCenter,
    submitText,
    cancelText,
    onSubmit,
    loading = false,
    hideFooter = false,
    hideCancel = false,
    size = 'md',
    closeOnOverlayClick = true,
    resizable = true,
    draggable = true,
    noPadding = false,
    wizard,
    tabs,
    aboveContent,
}) => {
    const { t } = useLanguage();

    // Detect mobile (phone only, not tablet — < 640px)
    const isMobile = useIsMobile();

    // Use translations as defaults if not provided
    const effectiveSubmitText = submitText || t.common?.save || 'Save';
    const effectiveCancelText = cancelText || t.common?.cancel || 'Cancel';
    const closeLabel = t.common?.close || 'Close';

    // Header gradient
    const headerBg = headerColor || 'from-cabaza-purple to-cabaza-pink';

    // Size presets for initial dimensions (height: 'auto' means fit content, max 90vh)
    const sizePresets = {
        sm: { width: 400, height: 'auto' },
        md: { width: 550, height: 'auto' },
        lg: { width: 750, height: 'auto' },
        xl: { width: 950, height: 'auto' },
        full: { width: window.innerWidth - 64, height: window.innerHeight - 64 }
    };

    const preset = sizePresets[size] || sizePresets.md;

    const {
        modalRef,
        modalStyle,
        isDragging,
        handleDragStart,
        handleResizeStart,
        initializeModal,
        reset,
    } = useDraggableResizable({
        minWidth: 350,
        minHeight: 250,
        initialWidth: preset.width,
        initialHeight: preset.height,
    });

    // Initialize modal position and size when opened
    useEffect(() => {
        if (isOpen) {
            requestAnimationFrame(() => {
                initializeModal();
            });
        } else {
            reset();
        }
    }, [isOpen, initializeModal, reset]);

    // Close on escape key
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && isOpen) {
                onClose();
            }
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, onClose]);

    // Prevent body scroll when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    if (!isOpen) return null;

    const handleOverlayClick = (e) => {
        if (closeOnOverlayClick && e.target === e.currentTarget) {
            onClose();
        }
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    // --- Shared sub-components ---

    const renderHeader = (rounded = true) => (
        <div
            className={`bg-gradient-to-r ${headerBg} p-4 sm:p-5 text-white select-none flex-shrink-0 ${
                rounded ? 'rounded-t-xl' : ''
            } ${!isMobile && draggable ? (isDragging ? 'cursor-grabbing' : 'cursor-grab') : ''}`}
            onMouseDown={!isMobile && draggable ? handleDragStart : undefined}
        >
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    {Icon && (
                        <div className="p-2.5 bg-white/20 rounded-lg flex-shrink-0">
                            <Icon className="w-5 h-5" />
                        </div>
                    )}
                    <div>
                        <h2 className="text-lg sm:text-xl font-bold leading-tight">{title}</h2>
                        {subtitle && <p className="text-[11px] text-white/80 mt-0.5">{subtitle}</p>}
                    </div>
                </div>
                <button
                    onClick={handleClose}
                    onMouseDown={(e) => e.stopPropagation()}
                    className="flex items-center justify-center min-w-11 min-h-11 sm:min-w-0 sm:min-h-0 p-2 hover:bg-white/20 rounded-full transition-colors flex-shrink-0"
                    aria-label={closeLabel}
                >
                    <X className="w-5 h-5" />
                </button>
            </div>
        </div>
    );

    const renderWizard = () => {
        if (!wizard) return null;
        const { steps, currentStep } = wizard;
        return (
            <div className="px-5 py-4 bg-slate-50/50 border-b border-slate-100 flex-shrink-0">
                <div className="flex items-center justify-between relative">
                    <div className="absolute top-1/2 left-0 w-full h-0.5 bg-slate-200 -z-0 transform -translate-y-1/2" />
                    {steps.map((step) => {
                        const StepIcon = step.icon;
                        const isCompleted = step.id < currentStep;
                        const isCurrent = step.id === currentStep;
                        return (
                            <div key={step.id} className="flex flex-col items-center gap-1.5 z-10">
                                <div className={`w-9 h-9 rounded-full flex items-center justify-center ring-4 ring-white transition-all duration-300 ${
                                    isCompleted
                                        ? 'bg-green-500 text-white shadow-md shadow-green-500/30'
                                        : isCurrent
                                            ? 'bg-cabaza-purple text-white shadow-md shadow-cabaza-purple/30'
                                            : 'bg-white border-2 border-slate-200 text-slate-400'
                                }`}>
                                    {isCompleted ? <Check className="w-3.5 h-3.5" /> : StepIcon ? <StepIcon className="w-3.5 h-3.5" /> : <span className="text-xs font-bold">{step.id + 1}</span>}
                                </div>
                                <span className={`text-[10px] font-medium text-center hidden sm:block ${
                                    isCurrent ? 'font-semibold text-cabaza-purple' : isCompleted ? 'text-green-600' : 'text-slate-500'
                                }`}>
                                    {step.label}
                                </span>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    const renderTabs = () => {
        if (!tabs) return null;
        const { items, activeTab, onChange } = tabs;
        return (
            <div className="flex border-b border-gray-200 flex-shrink-0">
                {items.map((tab) => {
                    const TabIcon = tab.icon;
                    return (
                        <button
                            key={tab.id}
                            onClick={() => onChange(tab.id)}
                            className={`flex-1 flex items-center justify-center gap-2 py-3 px-2 text-sm font-medium transition ${
                                activeTab === tab.id
                                    ? 'text-cabaza-purple border-b-2 border-cabaza-purple bg-purple-50'
                                    : 'text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            {TabIcon && <TabIcon className="w-4 h-4" />}
                            <span className="hidden sm:inline">{tab.label}</span>
                        </button>
                    );
                })}
            </div>
        );
    };

    const renderFooterContent = (rounded = true) => {
        if (hideFooter) return null;
        return (
            <div className={`border-t border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-900 flex-shrink-0 ${
                rounded ? 'rounded-b-xl' : 'pb-safe-4'
            }`}>
                {footer ? (
                    footer
                ) : (footerLeft || footerCenter || onSubmit || !hideCancel) ? (
                    <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2">
                            {footerLeft}
                        </div>
                        {footerCenter && (
                            <div className="flex items-center gap-2">
                                {footerCenter}
                            </div>
                        )}
                        <div className="flex items-center gap-2">
                            {!hideCancel && (
                                <Button variant="ghost" onClick={handleClose}>
                                    {effectiveCancelText}
                                </Button>
                            )}
                            {onSubmit && (
                                <Button variant="primary" onClick={onSubmit} loading={loading}>
                                    {effectiveSubmitText}
                                </Button>
                            )}
                        </div>
                    </div>
                ) : null}
            </div>
        );
    };

    // --- Mobile: full screen ---
    if (isMobile) {
        return (
            <div className="fixed inset-0 h-dvh z-[10000] flex flex-col bg-white dark:bg-gray-800 pt-safe">
                {renderHeader(false)}
                {renderWizard()}
                {renderTabs()}
                {aboveContent && <div className="flex-shrink-0">{aboveContent}</div>}
                <div className={`overflow-y-auto flex-1 ${noPadding ? '' : 'p-4'}`}>
                    {children}
                </div>
                {renderFooterContent(false)}
            </div>
        );
    }

    // --- Desktop/tablet ---
    return (
        <div className="fixed inset-0 z-[10000] overflow-hidden">
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black/30 transition-opacity"
                onClick={handleOverlayClick}
            />

            {/* Modal container */}
            <div className="flex min-h-full items-center justify-center p-4 pointer-events-none">
                <div
                    ref={modalRef}
                    style={modalStyle}
                    className={`relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl flex flex-col pointer-events-auto ${
                        !modalStyle.width ? 'w-full max-w-lg' : ''
                    }`}
                >
                    {/* Resize handles */}
                    {resizable && <ResizeHandles onResizeStart={handleResizeStart} />}

                    {renderHeader(true)}
                    {renderWizard()}
                    {renderTabs()}
                    {aboveContent && <div className="flex-shrink-0">{aboveContent}</div>}

                    {/* Content - scrollable */}
                    <div className={`overflow-y-auto flex-1 ${noPadding ? '' : 'p-4 sm:p-6'}`}>
                        {children}
                    </div>

                    {renderFooterContent(true)}
                </div>
            </div>
        </div>
    );
};

export default Modal;
